import React from "react";
import {
  Menu,
  X,
  Phone,
  Mail,
  MapPin,
  ChevronRight,
  Scale,
  Shield,
  BookOpen,
  Users,
  Gavel,
  BookCheck,
  Library,
  Award,
  FileText,
  Home,
  Heart,
  ShieldCheck,
  Stamp,
} from "lucide-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faWhatsapp } from "@fortawesome/free-brands-svg-icons";
import backgroundImage from "../assets/sora-1.jpg";
import justiceImage from "../assets/justice.jpg";

const LawFirmWebsite = () => {
  const [isMenuOpen, setIsMenuOpen] = React.useState(false);
  const [activeSection, setActiveSection] = React.useState("inicio");

  const menuItems = [
    { id: "inicio", label: "Início", icon: <Library size={16} /> },
    { id: "sobre", label: "Sobre", icon: <BookCheck size={16} /> },
    { id: "areas", label: "Áreas de Atuação", icon: <Scale size={16} /> },
    { id: "equipe", label: "Nossa Equipe", icon: <Users size={16} /> },
    { id: "contato", label: "Contato", icon: <Mail size={16} /> },
  ];

  const areas = [
    {
      icon: <Scale className="w-16 h-16 text-yellow-600" />,
      title: "Direito Civil",
      description:
        "O Direito Civil é a base das relações jurídicas e obrigações de ordem privada. Nossa equipe atua na elaboração de contratos, responsabilidade civil, disputas judiciais e extrajudiciais, sempre com o objetivo de mitigar riscos e assegurar a execução eficiente das obrigações, garantindo que seus direitos sejam protegidos.",
    },
    {
      icon: <Users className="w-16 h-16 text-yellow-600" />,
      title: "Direito de Família e Sucessões",
      description:
        "Cuidamos de questões como divórcios, guarda, pensão alimentícia, reconhecimento e dissolução de união estável, inventários e testamentos, seja no âmbito judicial ou extrajudicial. Nosso compromisso é oferecer um atendimento humanizado e eficiente para auxiliar você em momentos delicados.",
    },
    {
      icon: <Home className="w-16 h-16 text-yellow-600" />,
      title: "Direito Imobiliário",
      description:
        "Atuamos na compra, venda e locação de imóveis, regularizações junto à cartórios e Prefeituras, além de disputas relacionadas a contratos imobiliários. Oferecemos assessoria para garantir segurança jurídica e tranquilidade em todas as transações imobiliárias, evitando futuros problemas judiciais.",
    },
    {
      icon: <Heart className="w-16 h-16 text-yellow-600" />,
      title: "Direito Previdenciário",
      description:
        "Proporcionamos o acesso a benefícios como aposentadoria, auxílio-doença, pensão por morte e revisão de benefícios junto ao INSS. Nosso escritório trabalha para que você tenha seus direitos previdenciários reconhecidos, evitando burocracias e indeferimentos injustos.",
    },
    {
      icon: <ShieldCheck className="w-16 h-16 text-yellow-600" />,
      title: "Direito do Consumidor",
      description:
        "Protegemos consumidores e empresas em casos de publicidade enganosa, compras online, cobranças indevidas e problemas com produtos ou serviços. Garantimos o cumprimento do Código de Defesa do Consumidor, oferecendo defesa, assessoria preventiva e contenciosa para adequação às normas e gestão de riscos.",
    },
    {
      icon: <Stamp className="w-16 h-16 text-yellow-600" />,
      title: "Registro de Marca",
      description:
        "Proteja sua marca e seu negócio com o registro junto ao INPI (Instituto Nacional da Propriedade Industrial). Atendemos desde a pesquisa de viabilidade até o acompanhamento do processo de registro, garantindo exclusividade e proteção para sua identidade empresarial.",
    },
  ];

  const teamMembers = [
    {
      name: "Dra. Amanda Figueiredo Pinto de Oliveira",
      role: "",
      specialization: "Especialista em Direito Previdenciário",
      image: "/api/placeholder/300/300",
    },
    {
      name: "Dr. Walter Bombiere Pires",
      role: "Sócio Fundador",
      specialization: "Especialista em Direito Civil e Imobiliário",
      image: "/api/placeholder/300/300",
    },
    {
      name: "Dr. Matheus Mello Bombiere",
      role: "",
      specialization:
        "Especialista em Direito Imobiliário e Pós-graduando em Processo Civil",
      image: "/api/placeholder/300/300",
    },
  ];

  // Função para lidar com scroll suave
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
      setActiveSection(sectionId);
    }
    setIsMenuOpen(false);
  };

  const scrollToAbout = () => {
    const element = document.getElementById("sobre");

    if (element) {
      element.scrollIntoView({
        behavior: "smooth",
        block: "start",
      });
    }
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Header Aprimorado */}
      <header className="fixed w-full z-50">
        {/* Barra superior com informações de contato */}
        <div className="bg-yellow-600 text-white py-2">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex justify-between items-center">
            <div className="flex items-center space-x-4 text-sm">
              <div className="flex items-center">
                <Phone size={14} className="mr-2" />
                <span>(21) 99983-6229</span>
              </div>
              <div className="hidden md:flex items-center">
                <Mail size={14} className="mr-2" />
                <span>omatheusbombiere@gmail.com</span>
              </div>
            </div>
            <div className="hidden md:block">
              <Award size={20} className="text-white" />
            </div>
          </div>
        </div>

        {/* Navbar principal */}
        <nav className="bg-gray-900 text-white shadow-lg">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              {/* Logo */}
              <div className="flex items-center space-x-2">
                <Gavel className="text-yellow-600 h-8 w-8" />
                <div>
                  <div className="text-2xl font-serif">
                    Walter Bombiere
                    <span className="text-yellow-600">&</span>
                    Advogados
                  </div>
                  <div className="text-xs text-yellow-600">
                    Excelência em Advocacia
                  </div>
                </div>
              </div>

              {/* Desktop Menu */}
              <div className="hidden md:flex space-x-6">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`flex items-center space-x-2 px-3 py-2 rounded-md transition-colors ${
                      activeSection === item.id
                        ? "bg-yellow-600 text-white"
                        : "hover:text-yellow-600"
                    }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>

              {/* Mobile Menu Button */}
              <button
                onClick={() => setIsMenuOpen(!isMenuOpen)}
                className="md:hidden"
              >
                {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
              </button>
            </div>
          </div>

          {/* Mobile Menu */}
          {isMenuOpen && (
            <div className="md:hidden bg-gray-800 border-t border-gray-700">
              <div className="px-2 pt-2 pb-3 space-y-1">
                {menuItems.map((item) => (
                  <button
                    key={item.id}
                    onClick={() => scrollToSection(item.id)}
                    className={`w-full flex items-center space-x-3 px-3 py-2 rounded-md transition-colors ${
                      activeSection === item.id
                        ? "bg-yellow-600 text-white"
                        : "text-gray-300 hover:bg-gray-700"
                    }`}
                  >
                    {item.icon}
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}
        </nav>
      </header>

      {/* Espaçador para compensar o header fixo */}
      <div className="h-32"></div>

      {/* Hero Section */}
      <div
        id="inicio"
        className="relative bg-gray-800 text-white py-24 bg-cover bg-center"
        style={{
          backgroundImage: `url(${backgroundImage})`,
          backgroundBlendMode: "overlay",
        }}
      >
        {/* Dark overlay */}
        <div className="absolute inset-0 bg-gray-500 opacity-10"></div>

        {/* Content */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-2xl">
            <h1 className="text-5xl font-serif mb-6">
              Excelência e Dedicação em Advocacia
            </h1>
            <p className="text-xl mb-8 text-gray-300">
              Defendendo seus direitos com compromisso e profissionalismo desde
              1995
            </p>
            <div className="flex space-x-4">
              <button
                onClick={() =>
                  window.open("https://wa.me/+5521999836229", "_blank")
                }
                className="bg-yellow-600 text-white px-8 py-3 rounded hover:bg-yellow-700 transition-colors inline-flex items-center"
              >
                <span className="font-medium">Fale com um especialista</span>
                <ChevronRight className="ml-2" size={20} />
              </button>
              <button
                onClick={scrollToAbout}
                className="border border-yellow-600 text-yellow-600 px-8 py-3 rounded hover:bg-yellow-600 hover:text-white transition-colors inline-flex items-center"
              >
                Saiba Mais
                <FileText className="ml-2" size={20} />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Seção Sobre */}
      <div id="sobre" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-4xl font-serif mb-6 text-gray-900">
                Tradição e Excelência em Serviços Jurídicos
              </h2>
              <div className="space-y-4 text-gray-600">
                <p>
                  Fundado em 1995, o escritório Walter Bombiere & Advogados
                  construiu sua reputação sobre os pilares da ética, competência
                  e dedicação aos clientes. Nossa trajetória de mais de 30 anos
                  é marcada por casos expressivos e clientes satisfeitos.
                </p>
                <p>
                  Nosso compromisso com a excelência se reflete em uma equipe
                  altamente qualificada, composta por profissionais
                  especializados em diferentes áreas do direito, prontos para
                  oferecer soluções jurídicas personalizadas e eficientes.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-8 mt-12">
                <div className="border-l-4 border-yellow-600 pl-4">
                  <div className="text-4xl font-bold text-gray-900">30+</div>
                  <div className="text-gray-600">Anos de Experiência</div>
                </div>
                <div className="border-l-4 border-yellow-600 pl-4">
                  <div className="text-4xl font-bold text-gray-900">5+</div>
                  <div className="text-gray-600">Áreas de Atuação</div>
                </div>
              </div>

              <div className="mt-12 grid grid-cols-2 gap-6">
                <div className="flex items-start space-x-3">
                  <Shield className="text-yellow-600 h-6 w-6 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Compromisso</h3>
                    <p className="text-sm text-gray-600">
                      Dedicação total aos interesses de nossos clientes
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <BookOpen className="text-yellow-600 h-6 w-6 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Experiência</h3>
                    <p className="text-sm text-gray-600">
                      Profundo conhecimento jurídico e prática consolidada
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Scale className="text-yellow-600 h-6 w-6 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Ética</h3>
                    <p className="text-sm text-gray-600">
                      Conduta ética e transparente
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Users className="text-yellow-600 h-6 w-6 mt-1" />
                  <div>
                    <h3 className="font-semibold text-gray-900">Equipe</h3>
                    <p className="text-sm text-gray-600">
                      Profissionais altamente qualificados
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="relative">
              <div className="aspect-square rounded-lg overflow-hidden relative">
                <img
                  src={justiceImage}
                  alt="Background"
                  className="absolute inset-0 w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-br from-yellow-600/10 to-gray-900/90"></div>
                <div className="absolute bottom-8 left-8 right-8 text-white">
                  <BookOpen className="h-12 w-12 mb-4 text-yellow-600" />
                  <blockquote className="text-xl font-serif mb-4">
                    "Nosso compromisso é com a justiça e a excelência no
                    atendimento aos nossos clientes."
                  </blockquote>
                  <cite className="text-sm">
                    <div className="font-semibold">Dr. Walter Bombiere</div>
                    <div className="text-yellow-600">Sócio Fundador</div>
                  </cite>
                </div>
              </div>
              <div className="absolute -top-4 -right-4 h-full w-full bg-yellow-600/10 rounded-lg -z-10"></div>
            </div>
          </div>
        </div>
      </div>

      {/* Áreas de Atuação */}
      <div id="areas" className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-serif text-center mb-16 text-gray-900">
            Áreas de Atuação
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {areas.map((area, index) => (
              <div
                key={index}
                className="flex flex-col h-full p-6 bg-white border border-gray-200 rounded-lg hover:shadow-xl transition-shadow duration-300"
              >
                <div className="flex justify-center mb-6">{area.icon}</div>
                <h3 className="text-2xl font-semibold mb-4 text-center text-gray-900">
                  {area.title}
                </h3>
                <p className="text-gray-600 leading-relaxed flex-grow">
                  {area.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Nossa Equipe */}
      <div id="equipe" className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-serif text-center mb-16 text-gray-900">
            Nossa Equipe
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {teamMembers.map((member, index) => (
              <div
                key={index}
                className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-xl transition-shadow duration-300"
              >
                <div className="overflow-hidden"></div>
                <div className="p-6">
                  <h3 className="text-2xl font-semibold mb-2 text-gray-900">
                    {member.name}
                  </h3>
                  <div className="text-yellow-600 font-semibold mb-2">
                    {member.role}
                  </div>
                  <p className="text-gray-600">{member.specialization}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Contato */}
      <div id="contato" className="bg-gray-100 py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-4xl font-serif text-center mb-16">
            Entre em Contato
          </h2>
          <div className="grid md:grid-cols-3 gap-8">
            <div className="flex items-center justify-center space-x-4 p-6 bg-white rounded-lg shadow-sm">
              <Phone className="text-yellow-600" />
              <span>(21) 99983-6229</span>
            </div>
            <div className="flex items-center justify-center space-x-4 p-6 bg-white rounded-lg shadow-sm">
              <Mail className="text-yellow-600" />
              <span>omatheusbombiere@gmail.com</span>
            </div>
            <div className="flex flex-col items-center p-6 bg-white rounded-lg shadow-sm">
              <div className="flex items-center justify-center space-x-4 mb-4">
                <MapPin className="text-yellow-600" />
                <span>
                  Estr. de Jacarepaguá, 7655, sala 1222 (Edifício Unicenter) -
                  Freguesia (Jacarepaguá) - Rio de Janeiro
                </span>
              </div>
              <a
                href="https://www.google.com/maps/place/Estr.+de+Jacarepagu%C3%A1,+7655+-+Freguesia+(Jacarepagu%C3%A1),+Rio+de+Janeiro+-+RJ,+22755-155/@-22.9422788,-43.3442992,17z/data=!3m1!4b1!4m6!3m5!1s0x9bd833cc3160c7:0x345089728428ee5!8m2!3d-22.9422788!4d-43.3417243!16s%2Fg%2F11c2312gvt?entry=ttu&g_ep=EgoyMDI1MDMxMi4wIKXMDSoASAFQAw%3D%3D"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-yellow-600 text-white px-4 py-2 rounded flex items-center transition-colors hover:bg-yellow-700"
              >
                <MapPin className="mr-2" size={16} />
                Abrir no Google Maps
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-gray-900 text-white py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <p className="mb-2">
            © 2025 Walter Bombiere & Advogados. Todos os direitos reservados.
          </p>
          <p className="text-sm text-gray-400">
            Powered by{" "}
            <a
              href="https://3iapps.com.br"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-400 hover:text-white transition-colors duration-200"
            >
              3i Organization
            </a>
          </p>
        </div>
      </footer>

      <div className="fixed bottom-6 right-6 z-50">
        <a
          href="https://wa.me/+5521999836229"
          className="fixed w-16 h-16 bottom-6 right-6 bg-green-500 text-white rounded-full shadow-lg hover:bg-green-700 transition-colors z-50 flex items-center justify-center"
          target="_blank"
          rel="noopener noreferrer"
        >
          <FontAwesomeIcon icon={faWhatsapp} size="2x" />
        </a>
      </div>
    </div>
  );
};

export default LawFirmWebsite;
